import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '../../Layouts/Theme/Context/ThemeContextProvider';
import { Picture } from '../Picture/Picture';
import { ContentCardBlockVariant } from '~/Blocks/ContentCard/ContentCardBlockVariant.csharp';
import type { ContentCardLayoutProps } from '~/Blocks/ContentCardLayout/ContentCardLayout';
import { ContentCardLayout } from '~/Blocks/ContentCardLayout/ContentCardLayout';
import baseStyles from '~/Features/Blocks/ContentCardLayout/ContentCardBase.module.css';
import layoutStyles from '~/Features/Blocks/ContentCardLayout/ContentCardLayout.module.css';
import type { ContentCardWithImageViewModel } from '~/Partials/ContentCardWithImage/ContentCardWithImageViewModel.csharp';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import styles from './ContentCardWithImage.module.css';

export type ContentCardWithImageProps = ContentCardWithImageViewModel & Pick<ContentCardLayoutProps, 'stretched'>;

export const ContentCardWithImage: React.FC<React.PropsWithChildren<ContentCardWithImageProps>> = props => {
    const { image, imageFitBehavior, imageAlignment, orientation, contentCardViewModel, stretched } = props;

    const { variant } = contentCardViewModel;

    const theme = useTheme();
    const { withRoundedCorners, withBoxShadow } = theme;
    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLAnchorElement>();

    const isTransparent =
        variant === ContentCardBlockVariant.DarkTransparent || variant === ContentCardBlockVariant.LightTransparent;

    return (
        <div
            className={classNames(
                baseStyles[`Variant--${variant}`],
                withRoundedCorners && baseStyles['Variant--rounded'],
                withRoundedCorners && styles['Variant--rounded'],
                withBoxShadow && 'ContentCardBase--shadowed',
                styles.ContentCardWithImage,
                contentCardViewModel.callToActionLink?.href && [
                    baseStyles['Variant--hasLink'],
                    styles['Variant--hasLink'],
                    layoutStyles.CardWithLink,
                ],
                styles[`Variant--${imageAlignment}ImageAlignment`],
                styles[`Variant--${imageFitBehavior}Behavior`],
                styles[`Variant--${orientation}`],
                stretched && styles['Variant--stretched']
            )}
            role="none"
            onMouseDown={onProxyMouseDown}
            onMouseUp={onProxyMouseUp}
        >
            <div className={classNames(styles.ImageWrapper, isTransparent && styles['ImageWrapper--transparent'])}>
                {image ? (
                    <Picture {...image} className={styles.Image} />
                ) : (
                    <PicturePlaceholder className={styles.Image} />
                )}
            </div>
            <div className={styles.Layout}>
                <ContentCardLayout {...contentCardViewModel} stretched={stretched} linkRef={clickableRef} />
            </div>
        </div>
    );
};
